import { supabase } from '../lib/supabase';
import DOMPurify from 'dompurify';
import validator from 'validator';

/**
 * Security Service
 * Comprehensive security utilities for production hardening
 */

class SecurityService {
  constructor() {
    this.rateLimitCache = new Map();
    this.csrfTokens = new Map();
    this.sessionValidationInterval = 5 * 60 * 1000; // 5 minutes
  }

  // ============================================
  // XSS PROTECTION
  // ============================================

  /**
   * Sanitize HTML content to prevent XSS attacks
   */
  sanitizeHtml(dirty, options = {}) {
    const defaultConfig = {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span'],
      ALLOWED_ATTR: ['href', 'title', 'target'],
      ALLOW_DATA_ATTR: false,
      ...options
    };

    return DOMPurify?.sanitize(dirty, defaultConfig);
  }

  /**
   * Sanitize user input (text fields)
   */
  sanitizeInput(input) {
    if (typeof input !== 'string') return input;
    
    // Remove any HTML tags
    const stripped = input?.replace(/<[^>]*>/g, '');
    
    // Escape special characters
    return validator?.escape(stripped);
  }

  /**
   * Sanitize object recursively
   */
  sanitizeObject(obj) {
    if (typeof obj !== 'object' || obj === null) {
      return typeof obj === 'string' ? this.sanitizeInput(obj) : obj;
    }

    const sanitized = Array.isArray(obj) ? [] : {};
    
    for (const key in obj) {
      if (obj?.hasOwnProperty(key)) {
        sanitized[key] = this.sanitizeObject(obj?.[key]);
      }
    }

    return sanitized;
  }

  // ============================================
  // INPUT VALIDATION
  // ============================================

  /**
   * Validate email address
   */
  validateEmail(email) {
    return validator?.isEmail(email);
  }

  /**
   * Validate URL
   */
  validateUrl(url) {
    return validator?.isURL(url, {
      protocols: ['http', 'https'],
      require_protocol: true
    });
  }

  /**
   * Validate phone number
   */
  validatePhone(phone) {
    return validator?.isMobilePhone(phone, 'any');
  }

  /**
   * Validate password strength
   */
  validatePassword(password) {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/?.test(password);
    const hasLowerCase = /[a-z]/?.test(password);
    const hasNumbers = /\d/?.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/?.test(password);

    return {
      isValid: password?.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers,
      strength: this.calculatePasswordStrength(password),
      requirements: {
        minLength: password?.length >= minLength,
        hasUpperCase,
        hasLowerCase,
        hasNumbers,
        hasSpecialChar
      }
    };
  }

  /**
   * Calculate password strength score
   */
  calculatePasswordStrength(password) {
    let score = 0;
    
    if (password?.length >= 8) score += 1;
    if (password?.length >= 12) score += 1;
    if (/[a-z]/?.test(password)) score += 1;
    if (/[A-Z]/?.test(password)) score += 1;
    if (/\d/?.test(password)) score += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/?.test(password)) score += 1;

    if (score <= 2) return 'weak';
    if (score <= 4) return 'medium';
    return 'strong';
  }

  // ============================================
  // SQL INJECTION PREVENTION
  // ============================================

  /**
   * Validate and sanitize SQL query parameters
   * Note: Supabase uses parameterized queries by default, but this adds extra validation
   */
  validateQueryParam(param) {
    if (typeof param !== 'string') return param;

    // Check for common SQL injection patterns
    const sqlInjectionPatterns = [
      /('|(\-\-)|(;)|(\|\|)|(\*))/gi,
      /(union|select|insert|update|delete|drop|create|alter|exec|execute)/gi,
      /(script|javascript|onerror|onload)/gi
    ];

    for (const pattern of sqlInjectionPatterns) {
      if (pattern?.test(param)) {
        throw new Error('Invalid input detected');
      }
    }

    return param;
  }

  /**
   * Sanitize search query
   */
  sanitizeSearchQuery(query) {
    if (!query || typeof query !== 'string') return '';

    // Remove special characters that could be used in SQL injection
    return query?.replace(/[';"\\]/g, '')?.replace(/--/g, '')?.trim()?.substring(0, 200); // Limit length
  }

  // ============================================
  // CSRF PROTECTION
  // ============================================

  /**
   * Generate CSRF token
   */
  generateCsrfToken() {
    const token = crypto.randomUUID();
    const timestamp = Date.now();
    
    this.csrfTokens?.set(token, timestamp);
    
    // Clean up old tokens (older than 1 hour)
    this.cleanupCsrfTokens();
    
    return token;
  }

  /**
   * Validate CSRF token
   */
  validateCsrfToken(token) {
    if (!token) return false;

    const timestamp = this.csrfTokens?.get(token);
    if (!timestamp) return false;

    // Token expires after 1 hour
    const isValid = Date.now() - timestamp < 3600000;
    
    if (isValid) {
      // Remove token after use (one-time use)
      this.csrfTokens?.delete(token);
    }

    return isValid;
  }

  /**
   * Clean up expired CSRF tokens
   */
  cleanupCsrfTokens() {
    const now = Date.now();
    for (const [token, timestamp] of this.csrfTokens?.entries()) {
      if (now - timestamp > 3600000) {
        this.csrfTokens?.delete(token);
      }
    }
  }

  // ============================================
  // RATE LIMITING
  // ============================================

  /**
   * Check rate limit for an action
   */
  checkRateLimit(key, maxAttempts, windowMs) {
    const now = Date.now();
    const record = this.rateLimitCache?.get(key);

    if (!record) {
      this.rateLimitCache?.set(key, {
        attempts: 1,
        resetAt: now + windowMs
      });
      return { allowed: true, remaining: maxAttempts - 1 };
    }

    // Reset if window expired
    if (now > record?.resetAt) {
      this.rateLimitCache?.set(key, {
        attempts: 1,
        resetAt: now + windowMs
      });
      return { allowed: true, remaining: maxAttempts - 1 };
    }

    // Check if limit exceeded
    if (record?.attempts >= maxAttempts) {
      return {
        allowed: false,
        remaining: 0,
        resetAt: record?.resetAt
      };
    }

    // Increment attempts
    record.attempts++;
    this.rateLimitCache?.set(key, record);

    return {
      allowed: true,
      remaining: maxAttempts - record?.attempts
    };
  }

  /**
   * Rate limit API calls
   */
  async rateLimitApiCall(userId, endpoint, maxAttempts = 100, windowMs = 60000) {
    const key = `api:${userId}:${endpoint}`;
    return this.checkRateLimit(key, maxAttempts, windowMs);
  }

  // ============================================
  // SESSION SECURITY
  // ============================================

  /**
   * Validate session security
   */
  async validateSession() {
    try {
      const { data: { session }, error } = await supabase?.auth?.getSession();
      
      if (error || !session) {
        return { valid: false, error: 'No active session' };
      }

      // Check if token is about to expire (within 5 minutes)
      const expiresAt = session?.expires_at * 1000;
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      if (timeUntilExpiry < 5 * 60 * 1000) {
        // Refresh token
        const { data, error: refreshError } = await supabase?.auth?.refreshSession();
        if (refreshError) {
          return { valid: false, error: 'Session refresh failed' };
        }
        return { valid: true, session: data?.session, refreshed: true };
      }

      return { valid: true, session };
    } catch (error) {
      return { valid: false, error: error?.message };
    }
  }

  /**
   * Detect suspicious session activity
   */
  detectSuspiciousActivity(currentSession, previousSession) {
    const suspicious = [];

    // Check for rapid location changes (if IP tracking enabled)
    // Check for unusual access patterns
    // Check for multiple concurrent sessions

    return {
      isSuspicious: suspicious?.length > 0,
      reasons: suspicious
    };
  }

  // ============================================
  // FILE UPLOAD SECURITY
  // ============================================

  /**
   * Validate file upload
   */
  validateFileUpload(file, options = {}) {
    const {
      maxSize = 10 * 1024 * 1024, // 10MB default
      allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
      allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.pdf']
    } = options;

    const errors = [];

    // Check file size
    if (file?.size > maxSize) {
      errors?.push(`File size exceeds ${maxSize / 1024 / 1024}MB limit`);
    }

    // Check file type
    if (!allowedTypes?.includes(file?.type)) {
      errors?.push('File type not allowed');
    }

    // Check file extension
    const extension = '.' + file?.name?.split('.')?.pop()?.toLowerCase();
    if (!allowedExtensions?.includes(extension)) {
      errors?.push('File extension not allowed');
    }

    // Check for double extensions (e.g., file.pdf.exe)
    const parts = file?.name?.split('.');
    if (parts?.length > 2) {
      errors?.push('Multiple file extensions not allowed');
    }

    // Validate filename
    const sanitizedName = this.sanitizeFilename(file?.name);
    if (sanitizedName !== file?.name) {
      errors?.push('Invalid characters in filename');
    }

    return {
      valid: errors?.length === 0,
      errors,
      sanitizedName
    };
  }

  /**
   * Sanitize filename
   */
  sanitizeFilename(filename) {
    return filename?.replace(/[^a-zA-Z0-9.-]/g, '_')?.replace(/\.{2,}/g, '.')?.substring(0, 255);
  }

  /**
   * Check file content for malicious patterns
   */
  async scanFileContent(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const content = e.target.result;
        
        // Check for script tags in files
        const hasScript = /<script/i.test(content);
        const hasIframe = /<iframe/i.test(content);
        const hasOnEvent = /on\w+\s*=/i.test(content);
        
        resolve({
          safe: !hasScript && !hasIframe && !hasOnEvent,
          threats: [
            hasScript && 'Script tag detected',
            hasIframe && 'Iframe detected',
            hasOnEvent && 'Event handler detected'
          ].filter(Boolean)
        });
      };
      
      reader.onerror = () => {
        resolve({ safe: false, threats: ['File read error'] });
      };
      
      reader.readAsText(file.slice(0, 10000)); // Read first 10KB
    });
  }

  // ============================================
  // AUTHENTICATION SECURITY
  // ============================================

  /**
   * Detect brute force attempts
   */
  detectBruteForce(identifier, maxAttempts = 5, windowMs = 15 * 60 * 1000) {
    const key = `auth:${identifier}`;
    return this.checkRateLimit(key, maxAttempts, windowMs);
  }

  /**
   * Log security event
   */
  async logSecurityEvent(eventType, details) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      await supabase?.from('security_audit_log')?.insert({
        user_id: user?.id,
        event_type: eventType,
        details: details,
        ip_address: await this.getClientIp(),
        user_agent: navigator.userAgent,
        timestamp: new Date()?.toISOString()
      });
    } catch (error) {
      console.error('Failed to log security event:', error);
    }
  }

  /**
   * Get client IP address (best effort)
   */
  async getClientIp() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response?.json();
      return data?.ip;
    } catch {
      return 'unknown';
    }
  }

  // ============================================
  // SECURITY HEADERS
  // ============================================

  /**
   * Get recommended security headers
   */
  getSecurityHeaders() {
    return {
      'Content-Security-Policy': this.getContentSecurityPolicy(),
      'X-Frame-Options': 'DENY',
      'X-Content-Type-Options': 'nosniff',
      'Referrer-Policy': 'strict-origin-when-cross-origin',
      'Permissions-Policy': 'geolocation=(), microphone=(), camera=()',
      'Strict-Transport-Security': 'max-age=31536000; includeSubDomains'
    };
  }

  /**
   * Generate Content Security Policy
   */
  getContentSecurityPolicy() {
    const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
    const stripeUrl = 'https://js.stripe.com';
    
    return [
      "default-src 'self'",
      `connect-src 'self' ${supabaseUrl} https://api.stripe.com`,
      `script-src 'self' 'unsafe-inline' ${stripeUrl}`,
      "style-src 'self' 'unsafe-inline'",
      "img-src 'self' data: https: blob:",
      "font-src 'self' data:",
      "frame-src ${stripeUrl}",
      "object-src 'none'",
      "base-uri 'self'",
      "form-action 'self'"
    ]?.join('; ');
  }
}

export default new SecurityService();